import Image from 'next/image';

import { Share } from '@/icons';
import { NextLink } from '@/atoms';
import { IconButton } from '@/molecules';

import { DEFAULT_TOAST_OPTIONS_INFO } from '@/components/providers';
import { useToastDispatch } from '@/lib/hooks';
import { formatDateLong } from '@/lib/utils';
import FallBackIMG from '@/assets/images/fallback-product-card.jpg';

import type { FunctionComponent } from 'react';
import type { TypePostHeaderLeftProps } from './types';

const copy = (str: string) => navigator?.clipboard?.writeText(str);

/**
 * PostHeader
 */
export const PostHeaderLeft: FunctionComponent<TypePostHeaderLeftProps> = ({
  className = '',
  image,
  category,
  title,
  author,
  readingTime,
  date,
}: TypePostHeaderLeftProps) => {
  const { toast } = useToastDispatch();

  const copyWithToast = async () => {
    try {
      await copy(window.location.href);

      toast('Enlace copiado', {
        ...DEFAULT_TOAST_OPTIONS_INFO,
        link: undefined,
      });
    } catch {}
  };

  return (
    <div
      className={`relative flex h-[490px] w-full px-4 py-8 md:h-[435px] md:px-14 md:py-20 ${className}`}
    >
      <Image
        className="-z-10 object-cover"
        src={image?.src || FallBackIMG.src}
        alt={image?.alt || title}
        fill
      />

      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-black/40" />

      <div className="mt-auto flex max-w-[468px] flex-col gap-2 self-end text-white">
        {category && (
          <NextLink href={category.href}>
            <a className="u-actions u-actions--chips">{category.label}</a>
          </NextLink>
        )}

        <h1 className="u-headline u-headline--h1 line-clamp-5">{title}</h1>

        <div className="u-label u-label--overlline flex gap-2.5">
          {author && (
            <>
              <p>{author}</p>
              <span>·</span>
            </>
          )}

          {readingTime && (
            <>
              <time>{readingTime}min</time>
              <span>·</span>
            </>
          )}
          <time>{formatDateLong(date)}</time>
        </div>
      </div>

      <IconButton
        style="solid"
        size="medium"
        className="absolute right-6 top-6 bg-white text-typo-primary md:right-8 md:top-8"
        onClick={async () => {
          if (navigator.share) {
            try {
              await navigator.share({
                title,
                url: window.location.href,
              });
            } catch {
              copyWithToast();
            }
          } else copyWithToast();
        }}
      >
        <Share width={24} height={24} />
      </IconButton>
    </div>
  );
};

PostHeaderLeft.displayName = 'PostHeaderLeft';
